import { useEffect, useState } from "react";

import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { HttpStatusCode } from "axios";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowIcon from "@/assets/ArrowIcon";
import { DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import { refDataPrivilegeSchema } from "@/schemas/refDataPrivilegeSchema";
import { getRefDataPrivilegeById, postRefDataPrivilege, putRefDataPrivilege } from "@/services/refDataPrivilegeService";

type RefDataPrivilegeFormValues = {
    methodName: string;
};

const getErrorMessage = (error: unknown): string | undefined =>
    typeof error === "string" ? error : undefined;

const EditRefDataPrivilege = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        isOpen: isOpenConfirm,
        onOpen: onOpenConfirm,
        onClose: onCloseConfirm,
    } = useDisclosure();
    const toast = useToast();

    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'save' | 'cancel' | null>(null);
    const [pendingValues, setPendingValues] = useState<RefDataPrivilegeFormValues | null>(null);

    const { id } = location.state || {};
    const isEdit = Boolean(id);

    const fetchData = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const response = await getRefDataPrivilegeById(id) as any;
            if (response.status === HttpStatusCode.Ok && response.data) {
                setData(response.data);
            } else {
                toast({
                    title: 'Load error',
                    description: response?.message || "Failed to load RefData privilege method.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                navigate('/settings/ref-data-privileges');
            }
        } catch {
            toast({
                title: 'Load error',
                description: "An error occurred while loading the RefData privilege method.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            navigate('/settings/ref-data-privileges');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isEdit) {
            fetchData();
        }
    }, [id]);

    const handleSubmit = async (values: RefDataPrivilegeFormValues) => {
        setPendingValues(values);
        setConfirmAction('save');
        onOpenConfirm();
    };

    const handleConfirmSave = async (values: RefDataPrivilegeFormValues) => {
        try {
            const obj = {
                ...values,
                ...(isEdit && { id: data.id }),
            };

            const response = isEdit
                ? await putRefDataPrivilege(obj) as any
                : await postRefDataPrivilege(obj) as any;

            if (response.status === HttpStatusCode.Ok) {
                toast({
                    title: 'Success',
                    description: isEdit ? "RefData privilege method updated successfully." : "RefData privilege method created successfully.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                setTimeout(() => navigate('/settings/ref-data-privileges'), 700);
            } else {
                toast({
                    title: 'Save failed',
                    description: response?.message || "Failed to save RefData privilege method.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch {
            toast({
                title: 'Save error',
                description: "An error occurred while saving the RefData privilege method.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    };

    const handleCancel = () => {
        setConfirmAction('cancel');
        onOpenConfirm();
    };

    const handleConfirmCancel = () => {
        navigate('/settings/ref-data-privileges');
    };

    const initialValues: RefDataPrivilegeFormValues = data || {
        methodName: '',
    };

    return (
        <div style={{ padding: '20px 15px', maxWidth: '600px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', cursor: 'pointer' }} onClick={() => navigate('/settings/ref-data-privileges')}>
                <ArrowIcon style={{ transform: 'rotate(180deg)' }} />
                <span>Back</span>
            </div>

            <h2 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
                {isEdit ? 'Edit RefData Privilege Method' : 'Add RefData Privilege Method'}
            </h2>

            {isLoading && !data && <div>Loading...</div>}

            {!isLoading && (
                <Formik<RefDataPrivilegeFormValues>
                    initialValues={initialValues}
                    validationSchema={refDataPrivilegeSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ errors, touched, isSubmitting }) => (
                        <Form>
                            <div style={{ marginBottom: '15px' }}>
                                <Field name="methodName">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.methodName && touched.methodName)}>
                                            <FormLabel>Method Name *</FormLabel>
                                            <Input
                                                {...field}
                                                placeholder="e.g., GetAllPrivileges"
                                            />
                                            <FormErrorMessage>{getErrorMessage(errors.methodName)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    type="submit"
                                    colorScheme='blue'
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </Button>
                                <Button
                                    onClick={handleCancel}
                                    variant='outline'
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}

            <ModalConfirmation
                isOpen={isOpenConfirm}
                onClose={onCloseConfirm}
                title={confirmAction === 'save' ? 'Confirm Save' : 'Confirm Cancel'}
                confirmationText={confirmAction === 'save'
                    ? 'Are you sure you want to save this method?'
                    : 'Are you sure you want to cancel? Unsaved changes will be lost.'}
                handleCancel={onCloseConfirm}
                handleConfirm={() => {
                    if (confirmAction === 'save' && pendingValues) {
                        handleConfirmSave(pendingValues);
                    } else {
                        handleConfirmCancel();
                    }
                    onCloseConfirm();
                }}
                confirmButtonText={confirmAction === 'save' ? 'Save' : 'Yes'}
                cancelButtonText={confirmAction === 'save' ? 'Cancel' : 'No'}
            />
        </div>
    );
};

export default EditRefDataPrivilege;
